/**
 * ConsentScreen — interstitial gate shown after login when the user
 * is missing one or more mandatory consents. Common cases:
 *
 *   * Admin / staff invited via team-management (no public signup)
 *   * Pre-existing users from before the consent feature shipped
 *   * Anyone after a mandatory policy_version bump
 *
 * The user must accept every required consent to continue. Optional
 * consents can be ticked or skipped. On submit we POST every entered
 * decision and redirect back to where they were headed.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button, Spinner } from 'reactstrap';
import { FiShield } from 'react-icons/fi';
import consentService from '../../services/consentService';
import { allMandatoryAccepted } from './ConsentChecklist';
import { useTranslation } from '../../contexts/LanguageContext';
import { auth as authStyles } from '../../components/layout/authStyles';

const ConsentScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/dashboard';

  const [catalogue, setCatalogue] = useState([]);
  const [acceptance, setAcceptance] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // One checkbox accepts all mandatory consents (optional ones stay off);
  // each is still recorded per-item on submit for the DPDP trail.
  const [termsAccepted, setTermsAccepted] = useState(false);

  const toggleAcceptAll = (checked) => {
    setTermsAccepted(checked);
    setError(null);
    const next = {};
    if (checked) {
      catalogue.forEach((item) => {
        if (item.mandatory) next[item.code] = { read: true, accepted: true };
      });
    }
    setAcceptance(next);
  };

  const fetchCatalogue = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await consentService.getCatalogue();
      setCatalogue(res.data?.items ?? []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load consent catalogue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogue();
  }, []);

  const onSubmit = async () => {
    setError(null);
    if (!allMandatoryAccepted(catalogue, acceptance)) {
      setError(t(
        'page.consent.mustRead',
        'Open every required item and accept it before continuing.',
      ));
      return;
    }
    setSubmitting(true);
    try {
      await Promise.allSettled(
        catalogue.map((item) => {
          const accepted = acceptance[item.code]?.accepted;
          if (accepted === undefined) return Promise.resolve();
          return consentService.record({
            consent_type: item.code,
            consent_given: accepted,
            consent_text: item.summary,
            policy_version: item.version,
          });
        }),
      );
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save your consent. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={authStyles.card}>
      <span style={authStyles.cardEyebrow('#7C3AED')}>
        <FiShield size={12} /> {t('page.consent.eyebrow', 'Privacy & consent')}
      </span>
      <h1 style={authStyles.cardTitle}>
        {t('page.consent.title', 'A few quick consents before we continue')}
      </h1>
      <p style={authStyles.cardSub}>
        {t(
          'page.consent.subtitleSimple',
          'Khulja Sim Sim follows the Digital Personal Data Protection Act, 2023. Accept the required terms to continue — you can manage optional data preferences any time from Settings → Privacy.',
        )}
      </p>

      {error && <Alert fade={false} color="warning" className="py-2 mb-3 small">{error}</Alert>}

      {loading ? (
        <div className="text-center py-3"><Spinner size="sm" /></div>
      ) : catalogue.length === 0 ? (
        <Button color="link" size="sm" className="p-0" onClick={fetchCatalogue}>
          {t('page.common.retry', 'Retry')}
        </Button>
      ) : (
        <label
          className="d-flex align-items-start gap-2 p-3 rounded"
          style={{
            cursor: 'pointer',
            border: `1px solid ${termsAccepted ? '#7C3AED' : '#e2e8f0'}`,
            background: termsAccepted ? 'rgba(124,58,237,0.05)' : '#fff',
            transition: 'all 0.15s ease',
          }}
        >
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => toggleAcceptAll(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span className="small" style={{ color: '#374151', lineHeight: 1.55 }}>
            {t('page.consent.acceptPrefix', 'I have read and agree to the')}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#7C3AED', fontWeight: 600 }}>
              {t('page.register.termsLink', 'Terms of Service')}
            </a>
            {' '}{t('page.consent.and', 'and')}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#7C3AED', fontWeight: 600 }}>
              {t('page.register.privacyLink', 'Privacy Policy')}
            </a>.
          </span>
        </label>
      )}

      <Button
        color="primary"
        className="w-100 mt-3 fw-semibold"
        style={{ padding: '11px 18px', fontSize: '0.95rem' }}
        onClick={onSubmit}
        disabled={
          submitting ||
          loading ||
          catalogue.length === 0 ||
          !termsAccepted
        }
      >
        {submitting ? <Spinner size="sm" className="me-2" /> : null}
        {t('page.consent.btn.continue', 'Save & continue')}
      </Button>
    </div>
  );
};

export default ConsentScreen;
